package fr.labodoc.healthProfessional.webApp.pages.medicines

import fr.labodoc.healthProfessional.webApp.App
import fr.labodoc.healthProfessional.webApp.data.model.MedicineFlashInfoSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.MedicineInvitationSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.MedicineMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.MedicineModel
import fr.labodoc.healthProfessional.webApp.data.repository.MedicinesRepository
import fr.labodoc.healthProfessional.webApp.data.repository.MessagesRepository
import io.kvision.state.ObservableValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class MedicinePageViewModelImpl(
  private val medicineId: String,
  private val medicineRepository: MedicinesRepository,
  private val messageRepository: MessagesRepository,
  private val viewModelScope: CoroutineScope
) : MedicinePageViewModel() {
  override val medicineUiState: ObservableValue<MedicineUiState> by lazy {
    val observableValue: ObservableValue<MedicineUiState> = ObservableValue(MedicineUiState.Loading)

    App.Companion.scope.launch {
      medicineRepository
        .getMedicine(medicineId)
        .onLeft {
          observableValue.value = MedicineUiState.Error
        }
        .onRight { response ->
          val medicine = Medicine.fromModel(response)

          val laboratory = response.laboratory
            ?.let { laboratory ->
              Laboratory(
                id = laboratory.id,
                name = laboratory.name,
                websiteUrl = laboratory.websiteUrl,
                pharmacovigilanceUrl = laboratory.pharmacovigilanceUrl
              )
            }

          val dosages = response.dosages
            .map { dosage ->
              Dosage(
                cisCode = dosage.cisCode,
                name = dosage.name
              )
            }

          val marketingDocuments = response.marketingDocuments
            .map { marketingDocument ->
              MarketingDocument(
                id = marketingDocument.id,
                name = marketingDocument.name,
                url = marketingDocument.url,
                seenLatestVersion = marketingDocument.seenLatestVersion
              )
            }

          observableValue.value = MedicineUiState.Loaded(
            medicine = medicine,
            laboratory = laboratory,
            dosages = dosages,
            marketingDocuments = marketingDocuments
          )
        }
    }

    observableValue
  }

  override fun tagDocumentAsSeen(id: String) {
    App.Companion.scope.launch {
      medicineRepository
        .tagDocumentAsSeen(id, medicineId)
    }
  }


  override val messageListUiState: MutableStateFlow<MessageListUiState> by lazy {
    fetchMessages()
    MutableStateFlow(MessageListUiState.Loading)
  }

  override val messageListEvents: MutableSharedFlow<MessageEvent> = MutableSharedFlow()

  private fun fetchMessages() {
    viewModelScope.launch {
      messageListUiState.value = messageRepository
        .getMessagesForMedicine(medicineId)
        .fold(
          {
            MessageListUiState.Error
          },
          { response ->
            MessageListUiState.Loaded(
              messages = response
                .map(Message.Companion::fromModel)
                .associateBy(Message::id)
            )
          }
        )
    }
  }

  override fun setMessageFavoriteStatus(messageId: String, favoriteStatus: Boolean) {
    viewModelScope.launch {
      val response = if (favoriteStatus)
        messageRepository.favorite(messageId)
      else
        messageRepository.unfavorite(messageId)

      val correctFavoriteStatus = response
        .fold(
          {
            messageListEvents.emit(MessageEvent.SetFavoriteStatusFailed)
            !favoriteStatus
          },
          {
            favoriteStatus
          }
        )

      updateMessage(messageId) {
        it.copy(favorite = correctFavoriteStatus)
      }
    }
  }

  override fun getMessage(messageId: String) {
    viewModelScope.launch {
      messageRepository.getMessage(messageId)
        .onRight {
          updateMessage(messageId) {
            it.copy(seen = true)
          }
          messageListEvents.emit(MessageEvent.OpenMessage(it))
        }
    }
  }

  private fun updateMessage(messageId: String, block: (Message) -> Message) {
    messageListUiState.update { currentUiState ->
      when (currentUiState) {
        is MessageListUiState.Loaded -> {
          currentUiState.copy(
            messages = currentUiState.messages.toMutableMap().apply {
              this[messageId]?.let {
                this[messageId] = block(it)

              }
            }
          )
        }

        else -> currentUiState
      }
    }
  }
}

private fun MedicinePageViewModel.Medicine.Companion.fromModel(medicine: MedicineModel): MedicinePageViewModel.Medicine {
  fun test(atcClassification: MedicineModel.AtcClassification): MedicinePageViewModel.Medicine.AtcClassification {
    return MedicinePageViewModel.Medicine.AtcClassification(
      code = atcClassification.code,
      name = atcClassification.name,
      parent = atcClassification.parent?.let { test(it) }
    )
  }

  return MedicinePageViewModel.Medicine(
    id = medicine.id,
    name = medicine.name,
    atcClassification = test(medicine.atcClassification),
    logoUrl = medicine.logoUrl
  )
}

private fun MedicinePageViewModel.Message.Companion.fromModel(message: MedicineMessageSummaryModel): MedicinePageViewModel.Message {
  return MedicinePageViewModel.Message(
    id = message.id,
    type = when (message) {
      is MedicineFlashInfoSummaryModel -> MedicinePageViewModel.Message.Type.FlashInfo
      is MedicineInvitationSummaryModel -> MedicinePageViewModel.Message.Type.Invitation
    },
    title = message.title,
    publishedAt = message.publishedAt,
    favorite = message.favorite,
    seen = message.seen
  )
}
